import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getCompanyRolePermissions } from "@/services/companyRolePermissionService";
import { getActivePermissionPages } from "@/services/permissionPageService";
import { permissionRowsToByCode } from "@/utils/permissionRowsToByCode";

const initialState = {
  byCode: {},
  bypass: false,
  status: "idle",
  error: null,
  loadedRoleId: null,
  activePages: [],
  activePagesStatus: "idle",
  activePagesError: null,
};

export const fetchActivePermissionPages = createAsyncThunk(
  "permissions/fetchActivePermissionPages",
  async (_, { rejectWithValue }) => {
    try {
      const list = await getActivePermissionPages();
      return Array.isArray(list) ? list : [];
    } catch (err) {
      const msg =
        err?.response?.data?.message || err.message || "Failed to load permission pages";
      return rejectWithValue(msg);
    }
  },
  {
    condition: (_, { getState }) => {
      const p = getState().permissions;
      if (p.activePagesStatus === "loading") return false;
      if (p.activePagesStatus === "succeeded") return false;
      return true;
    },
  }
);

export const fetchMyRolePermissions = createAsyncThunk(
  "permissions/fetchMyRolePermissions",
  async (companyRoleId, { rejectWithValue }) => {
    try {
      const res = await getCompanyRolePermissions(companyRoleId);
      const rows = res?.data;
      const list = Array.isArray(rows) ? rows : [];
      return { byCode: permissionRowsToByCode(list) };
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Failed to load permissions";
      return rejectWithValue(msg);
    }
  },
  {
    condition: (companyRoleId, { getState }) => {
      if (companyRoleId == null || companyRoleId === "") return false;
      const p = getState().permissions;
      if (p.loadedRoleId === Number(companyRoleId) && p.status === "succeeded" && !p.bypass) {
        return false;
      }
      return true;
    },
  }
);

const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    setPermissionsBypass: (state, action) => {
      state.bypass = !!action.payload;
      state.byCode = {};
      state.status = "succeeded";
      state.error = null;
      state.loadedRoleId = null;
    },
    resetPermissions: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyRolePermissions.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMyRolePermissions.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.bypass = false;
        state.byCode = action.payload.byCode || {};
        state.loadedRoleId = Number(action.meta.arg);
        state.error = null;
      })
      .addCase(fetchMyRolePermissions.rejected, (state, action) => {
        state.status = "failed";
        state.bypass = false;
        state.byCode = {};
        state.error = action.payload || "Failed to load permissions";
        state.loadedRoleId = null;
      })
      .addCase(fetchActivePermissionPages.pending, (state) => {
        state.activePagesStatus = "loading";
        state.activePagesError = null;
      })
      .addCase(fetchActivePermissionPages.fulfilled, (state, action) => {
        state.activePagesStatus = "succeeded";
        state.activePages = action.payload || [];
        state.activePagesError = null;
      })
      .addCase(fetchActivePermissionPages.rejected, (state, action) => {
        state.activePagesStatus = "failed";
        state.activePages = [];
        state.activePagesError = action.payload || "Failed to load permission pages";
      })
      .addMatcher(
        (action) => action.type === "auth/logoutUser/fulfilled",
        () => initialState
      );
  },
});

export const { setPermissionsBypass, resetPermissions } = permissionsSlice.actions;
export default permissionsSlice.reducer;

/** @param {import('@reduxjs/toolkit').PayloadAction<{ user?: object }>} _ */
export function selectPermissionAllowed(state, code, actionKey = "view") {
  const p = state.permissions;
  if (!code) return true;
  if (p.bypass) return true;
  const row = p.byCode[code];
  if (!row) return false;
  return !!row[actionKey];
}

export function selectPermissionsReady(state) {
  const p = state.permissions;
  if (p.bypass) return true;
  const pagesReady =
    p.activePagesStatus === "succeeded" || p.activePagesStatus === "failed";
  const matrixReady = p.status === "succeeded" || p.status === "failed";
  return pagesReady && matrixReady;
}

export function selectPermissionsLoading(state) {
  return state.permissions.status === "loading";
}
